import { Select, Input, Button, Divider } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  propiedadesApi,
  type GeoCountry,
  type GeoRegion,
  type GeoComuna,
  type Sector,
} from "../services/propiedades.api";

export type GeoValue = {
  region: number | null;
  comuna: number | null;
  sector: number | null;
};

type Props = {
  value: GeoValue;
  onChange: (v: GeoValue) => void;
  size?: "small" | "middle" | "large";
};

// Cache a nivel módulo: la geografía (país/región/comuna/sector) cambia rarísimo,
// así que la cargamos UNA vez y la reusamos en todos los GeoSelect de la sesión.
// Es LA MISMA estructura que usan las propiedades (tablas region/comuna/sector).
type GeoCache = {
  countries: GeoCountry[];
  regions: GeoRegion[];
  comunas: GeoComuna[];
  sectores: Sector[];
};
let _geoCache: GeoCache | null = null;
let _geoPromise: Promise<void> | null = null;

async function loadGeo() {
  if (_geoCache) return;
  if (!_geoPromise) {
    _geoPromise = (async () => {
      const [co, r, c, s] = await Promise.all([
        propiedadesApi.getCountries(),
        propiedadesApi.getRegions(),
        propiedadesApi.getComunas(),
        propiedadesApi.getSectores(),
      ]);
      _geoCache = {
        countries: co?.data ?? [],
        regions: r?.data ?? [],
        comunas: c?.data ?? [],
        sectores: s?.data ?? [],
      };
    })();
  }
  await _geoPromise;
}

/**
 * Selector geográfico en cascada (Región → Comuna → Sector) reutilizable, sobre la
 * MISMA estructura que las propiedades (tablas + endpoints `/api/propiedades/*`).
 * Trabaja con IDs y filtra cada nivel por el padre. Permite CREAR inline en cada
 * nivel (como el panel de propiedades) — clave porque los sectores son escasos y
 * suelen crearse sobre la marcha. Round 2026-06-30.
 */
export default function GeoSelect({ value, onChange, size = "middle" }: Props) {
  const [geo, setGeo] = useState<GeoCache | null>(_geoCache);
  const [newRegion, setNewRegion] = useState("");
  const [newComuna, setNewComuna] = useState("");
  const [newSector, setNewSector] = useState("");
  const [busy, setBusy] = useState(false);
  const regionRef = useRef<any>(null);
  const comunaRef = useRef<any>(null);
  const sectorRef = useRef<any>(null);

  useEffect(() => {
    let alive = true;
    if (!_geoCache) {
      loadGeo().then(() => {
        if (alive) setGeo(_geoCache ? { ..._geoCache } : null);
      });
    }
    return () => {
      alive = false;
    };
  }, []);

  const bump = () => setGeo(_geoCache ? { ..._geoCache } : null);

  const regionOpts = useMemo(
    () => (geo?.regions ?? []).map((r) => ({ value: r.id, label: r.region ?? `#${r.id}` })),
    [geo],
  );
  const comunaOpts = useMemo(
    () =>
      (geo?.comunas ?? [])
        .filter((c) => value.region == null || c.region === value.region)
        .map((c) => ({ value: c.id, label: c.comuna ?? `#${c.id}` })),
    [geo, value.region],
  );
  const sectorOpts = useMemo(
    () =>
      (geo?.sectores ?? [])
        .filter((s) => value.comuna == null || s.comuna === value.comuna)
        .map((s) => ({ value: s.id, label: s.sector ?? `#${s.id}` })),
    [geo, value.comuna],
  );

  // ── Creación inline (misma API que propiedades) ────────────────────────────

  const addRegion = async () => {
    const nombre = newRegion.trim();
    const paisId = geo?.countries?.[0]?.id;
    if (!nombre || !paisId || busy) return;
    setBusy(true);
    const res = await propiedadesApi.createRegion(nombre, paisId);
    setBusy(false);
    if (res?.data && _geoCache) {
      _geoCache.regions.push(res.data);
      setNewRegion("");
      bump();
      onChange({ region: res.data.id, comuna: null, sector: null });
      regionRef.current?.blur?.();
    }
  };

  const addComuna = async () => {
    const nombre = newComuna.trim();
    if (!nombre || value.region == null || busy) return;
    setBusy(true);
    const res = await propiedadesApi.createComuna(nombre, value.region);
    setBusy(false);
    if (res?.data && _geoCache) {
      _geoCache.comunas.push(res.data);
      setNewComuna("");
      bump();
      onChange({ ...value, comuna: res.data.id, sector: null });
      comunaRef.current?.blur?.();
    }
  };

  const addSector = async () => {
    const nombre = newSector.trim();
    if (!nombre || value.comuna == null || busy) return;
    setBusy(true);
    const res = await propiedadesApi.createSector(nombre, value.comuna);
    setBusy(false);
    if (res?.data && _geoCache) {
      _geoCache.sectores.push(res.data);
      setNewSector("");
      bump();
      onChange({ ...value, sector: res.data.id });
      sectorRef.current?.blur?.();
    }
  };

  const addRow = (
    val: string,
    setVal: (v: string) => void,
    onAdd: () => void,
    placeholder: string,
    disabled: boolean,
  ) => (
    <div style={{ display: "flex", gap: 4, padding: "4px 8px" }} onMouseDown={(e) => e.preventDefault()}>
      <Input
        size="small"
        placeholder={placeholder}
        value={val}
        disabled={disabled}
        onChange={(e) => setVal(e.target.value)}
        onPressEnter={onAdd}
      />
      <Button
        size="small"
        type="primary"
        icon={<PlusOutlined />}
        loading={busy}
        disabled={disabled || !val.trim()}
        onClick={onAdd}
      >
        Crear
      </Button>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <Select
        ref={regionRef}
        size={size}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Región"
        style={{ minWidth: 150, flex: 1 }}
        value={value.region ?? undefined}
        options={regionOpts}
        onChange={(region) => onChange({ region: region ?? null, comuna: null, sector: null })}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            {addRow(newRegion, setNewRegion, addRegion, "Nueva región…", !geo?.countries?.length)}
          </>
        )}
      />
      <Select
        ref={comunaRef}
        size={size}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Comuna"
        style={{ minWidth: 150, flex: 1 }}
        value={value.comuna ?? undefined}
        disabled={value.region == null}
        options={comunaOpts}
        onChange={(comuna) => onChange({ ...value, comuna: comuna ?? null, sector: null })}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            {addRow(newComuna, setNewComuna, addComuna, "Nueva comuna…", value.region == null)}
          </>
        )}
      />
      <Select
        ref={sectorRef}
        size={size}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Sector"
        style={{ minWidth: 150, flex: 1 }}
        value={value.sector ?? undefined}
        disabled={value.comuna == null}
        options={sectorOpts}
        onChange={(sector) => onChange({ ...value, sector: sector ?? null })}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            {addRow(newSector, setNewSector, addSector, "Nuevo sector…", value.comuna == null)}
          </>
        )}
      />
    </div>
  );
}
